import React, { useState, useEffect, useRef } from 'react'
import { useHistory } from 'react-router-dom'
import ElementsDataService from '../../api/entitiesDataServices/ElementsDataService.js'
import AuthenticationService from '../general/AuthenticationService.js'
import * as XLSX from 'xlsx'

const T = {
    bg:'#f1f5f9', card:'#ffffff', border:'#e8edf5',
    blue:'#3b82f6', blueD:'#2563eb', navy:'#0f172a',
    txt:'#1e293b', txt2:'#64748b', muted:'#94a3b8',
    green:'#16a34a', greenBg:'#f0fdf4', greenBdr:'#bbf7d0',
    r:16, rsm:10,
    shadow:'0 1px 3px rgba(15,23,42,.06)',
    font:"'Inter',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif",
}

function fmt(v) {
    if (v === null || v === undefined || isNaN(v)) return '—'
    const n = Number(v)
    if (n === 0) return '—'
    return n.toLocaleString('es-MX', { minimumFractionDigits:2, maximumFractionDigits:2 })
}

/* ─── SingleSelect con búsqueda ─── */
function SociedadSelect({ options, value, onChange }) {
    const [open,  setOpen]  = useState(false)
    const [query, setQuery] = useState('')
    const inputRef     = useRef(null)
    const containerRef = useRef(null)

    useEffect(() => {
        const h = e => { if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false) }
        document.addEventListener('mousedown', h)
        return () => document.removeEventListener('mousedown', h)
    }, [])

    const filtered = options.filter(o =>
        (`${o.razonSocial} ${o.rfc}`).toLowerCase().includes(query.toLowerCase())
    )

    const handleSelect = o => {
        onChange(o)
        setOpen(false)
        setQuery('')
    }

    return (
        <div ref={containerRef} style={{ position:'relative', flex:1 }}>
            <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.txt2,
                marginBottom:4, textTransform:'uppercase', letterSpacing:'.05em' }}>Sociedad</label>
            <div onClick={() => { setOpen(o => !o); setTimeout(() => inputRef.current?.focus(), 50) }}
                style={{ minHeight:40, padding:'6px 12px', borderRadius:T.rsm,
                    border:`1px solid ${open ? T.blue : T.border}`, background:T.card, cursor:'pointer',
                    display:'flex', alignItems:'center', justifyContent:'space-between', gap:8,
                    boxShadow: open ? `0 0 0 3px ${T.blue}20` : T.shadow, transition:'all .15s' }}>
                {value
                    ? <span style={{ fontSize:13, color:T.txt, fontWeight:500 }}>{value.razonSocial} <span style={{ color:T.muted, fontFamily:'monospace', fontSize:12 }}>{value.rfc}</span></span>
                    : <span style={{ fontSize:13, color:T.muted }}>Selecciona una sociedad...</span>
                }
                <span style={{ color:T.muted, fontSize:12 }}>{open ? '▲' : '▼'}</span>
            </div>
            {open && (
                <div style={{ position:'absolute', top:'100%', left:0, right:0, zIndex:200, marginTop:4,
                    background:T.card, border:`1px solid ${T.border}`, borderRadius:T.rsm,
                    boxShadow:'0 8px 24px rgba(15,23,42,.12)', overflow:'hidden' }}>
                    <div style={{ padding:'8px 12px', borderBottom:`1px solid ${T.border}` }}>
                        <input ref={inputRef} value={query} onChange={e => setQuery(e.target.value)}
                            placeholder="Buscar por razón social o RFC..."
                            onMouseDown={e => e.stopPropagation()}
                            style={{ width:'100%', border:'none', outline:'none', fontSize:13,
                                color:T.txt, fontFamily:T.font, background:'transparent' }} />
                    </div>
                    <div style={{ maxHeight:260, overflowY:'auto' }}>
                        {filtered.length === 0
                            ? <div style={{ padding:'12px 14px', fontSize:13, color:T.muted }}>Sin resultados</div>
                            : filtered.map(o => (
                                <div key={o.rfc} onMouseDown={e => { e.preventDefault(); handleSelect(o) }}
                                    style={{ padding:'10px 14px', fontSize:13, cursor:'pointer',
                                        background: value?.rfc === o.rfc ? `${T.blue}08` : 'transparent',
                                        color: value?.rfc === o.rfc ? T.blue : T.txt,
                                        fontWeight: value?.rfc === o.rfc ? 600 : 400,
                                        borderBottom:`1px solid ${T.border}` }}>
                                    <span style={{ fontWeight:500 }}>{o.razonSocial}</span>
                                    <span style={{ color:T.muted, fontFamily:'monospace', fontSize:11, marginLeft:8 }}>{o.rfc}</span>
                                </div>
                            ))
                        }
                    </div>
                </div>
            )}
        </div>
    )
}

const GROUPS = [
    { label:'FECHAS',                           cols:4, bg:'#2563eb' },
    { label:'INFORMACIÓN GENERAL CONTABLE',     cols:8, bg:'#3b82f6' },
    { label:'R1 ISR',                           cols:3, bg:'#7c3aed' },
    { label:'R12 ISR SUE. Y SAL.',              cols:4, bg:'#6d28d9' },
    { label:'R13 ISR IAS',                      cols:4, bg:'#7c3aed' },
    { label:'R14 ISR RET. SP',                  cols:3, bg:'#6d28d9' },
    { label:'R15 ISR RET. ARR.',                cols:3, bg:'#7c3aed' },
    { label:'R21 IVA',                          cols:4, bg:'#0891b2' },
    { label:'R21 IVA RFN',                      cols:4, bg:'#0e7490' },
    { label:'R24 IVA RET.',                     cols:3, bg:'#0891b2' },
    { label:'IVA SIMP. CONFIANZA',              cols:4, bg:'#059669' },
    { label:'ISR SIMP. CONFIANZA',              cols:3, bg:'#047857' },
]

const COL_HEADERS = [
    'N°','Fecha PPM','Periodo/Ejercicio','Complementarias',
    'Ing. Dec. SAT','Ing. Fact. SAT','Ing. a Disminuir','CFDIs I Metadata','Ing. Acum SAT','CFDIs I Acum','CFDIs N Metadata','CFDIs N Acum',
    'ISR PM','Recargos','Total Pagar',
    'ISR Sue.','Recargos','Subsidio','Total',
    'ISR IAS','Recargos','Subsidio','Total',
    'ISR Ret SP','Recargos','Total',
    'ISR Ret Arr','Recargos','Total',
    'IVA Cargo','IVA Favor','Recargos','Total',
    'IVA RFN Cargo','IVA RFN Favor','Recargos','Total',
    'IVA Ret','Recargos','Total',
    'IVA SC Cargo','IVA SC Favor','Recargos','Total',
    'ISR SC','Recargos','Total',
]

export default function RelacionDeclaracionesComponent() {
    const history = useHistory()

    const [allSociedades, setAllSociedades] = useState([])
    const [sociedad,      setSociedad]      = useState(null)
    const [declaraciones, setDeclaraciones] = useState([])
    const [loading,       setLoading]       = useState(false)
    const [loadingCat,    setLoadingCat]    = useState(true)

    useEffect(() => {
        if (!AuthenticationService.haveAuthorization('READ_CONTABILIDAD')) {
            history.push('/login'); return
        }
        ElementsDataService.retrieveAllElements('sociedades')
            .then(r => setAllSociedades(
                (r.data || []).map(s => ({ id:s.id, razonSocial:s.razonSocial, rfc:s.rfc }))
            ))
            .finally(() => setLoadingCat(false))
    }, []) // eslint-disable-line

    const handleSociedadChange = soc => {
        setSociedad(soc)
        if (!soc) { setDeclaraciones([]); return }
        setLoading(true)
        setDeclaraciones([])
        ElementsDataService.retrieveElement('cfdis/declaraciones', soc.rfc)
            .then(r => setDeclaraciones(r.data || []))
            .finally(() => setLoading(false))
    }

    /* ── Excel ── */
    const downloadExcel = () => {
        const data = declaraciones.map((d, i) => {
            const complementarias = d.declaracionesProvisonalesDelPeriodoEjercicio
                ?.map(dp => `${dp.tipoDeclaracion} ${dp.fechaHora}`).join(' | ') || ''
            return {
                'N°':                        i + 1,
                'Fecha PPM':                 d.fechaPresentacionPPM || '',
                'Periodo/Ejercicio':         `${d.periodo}/${d.ejercicio}`,
                'Última Presentación':       d.fechaUltimaPresentacionPeriodoEjercicio || '',
                'Complementarias':           complementarias,
                'Ing. Dec. SAT':             d.ingresosNominalesMesDeclarado ?? '',
                'Ing. Fact. SAT':            d.ingresosNominalesFacturados ?? '',
                'Ing. a Disminuir':          d.ingresosNominalesaDisminuir ?? '',
                "CFDIs I Metadata":          d.cfdisIngresosMetadadata ?? '',
                'Ing. Acum SAT':             d.totalIngresosNominales ?? '',
                "CFDIs I Acum":              d.cfdisIngresosAcumuladosMetadadata ?? '',
                "CFDIs N Metadata":          d.cfdisNominaMetadadata ?? '',
                "CFDIs N Acum":              d.cfdisNominaAcumuladaMetadadata ?? '',
                'R1 ISR':                    d.r1Acargo ?? '',
                'R1 Recargos':               d.r1Recargos ?? '',
                'R1 Total':                  d.r1CantidadAPagar ?? '',
                'R12 Sue.':                  d.r12Acargo ?? '',
                'R12 Recargos':              d.r12Recargos ?? '',
                'R12 Subsidio':              d.r12Subsidio ?? '',
                'R12 Total':                 d.r12CantidadAPagar ?? '',
                'R13 IAS':                   d.r13Acargo ?? '',
                'R13 Recargos':              d.r13Recargos ?? '',
                'R13 Subsidio':              d.r13Subsidio ?? '',
                'R13 Total':                 d.r13CantidadAPagar ?? '',
                'R14 Ret SP':                d.r14Acargo ?? '',
                'R14 Recargos':              d.r14Recargos ?? '',
                'R14 Total':                 d.r14CantidadAPagar ?? '',
                'R15 Ret Arr':               d.r15Acargo ?? '',
                'R15 Recargos':              d.r15Recargos ?? '',
                'R15 Total':                 d.r15CantidadAPagar ?? '',
                'R21 IVA Cargo':             d.r21Acargo ?? '',
                'R21 IVA Favor':             d.r21Afavor ?? '',
                'R21 Recargos':              d.r21Recargos ?? '',
                'R21 Total':                 d.r21CantidadAPagar ?? '',
                'R21 RFN Cargo':             d.r21AcargoRFN ?? '',
                'R21 RFN Favor':             d.r21AfavorRFN ?? '',
                'R21 RFN Recargos':          d.r21RecargosRFN ?? '',
                'R21 RFN Total':             d.r21CantidadAPagarRFN ?? '',
                'R24 Ret':                   d.r24Acargo ?? '',
                'R24 Recargos':              d.r24Recargos ?? '',
                'R24 Total':                 d.r24CantidadAPagar ?? '',
                'IVA SC Cargo':              d.ivaSimplificadoConfianzaAcargo ?? '',
                'IVA SC Favor':              d.ivaSimplificadoConfianzaAfavor ?? '',
                'IVA SC Recargos':           d.ivaSimplificadoConfianzaRecargos ?? '',
                'IVA SC Total':              d.ivaSimplificadoConfianzaCantidadAPagar ?? '',
                'ISR SC':                    d.isrSimplificadoConfianzaAcargo ?? '',
                'ISR SC Recargos':           d.isrSimplificadoConfianzaRecargos ?? '',
                'ISR SC Total':              d.isrSimplificadoConfianzaCantidadAPagar ?? '',
            }
        })
        const ws = XLSX.utils.json_to_sheet(data)
        ws['!cols'] = Object.keys(data[0] || {}).map(() => ({ wch: 14 }))
        ws['!cols'][0] = { wch:4 }
        ws['!cols'][2] = { wch:18 }
        ws['!cols'][4] = { wch:28 }
        const wb = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(wb, ws, sociedad?.rfc || 'Declaraciones')
        XLSX.writeFile(wb, `Declaraciones_${sociedad?.rfc}_${new Date().toISOString().slice(0,10)}.xlsx`)
    }

    /* ── estilos ── */
    const thG = bg => ({ padding:'7px 10px', fontSize:10, fontWeight:800, color:'white', background:bg,
        textAlign:'center', borderRight:`1px solid rgba(255,255,255,.25)`, whiteSpace:'nowrap' })
    const thS = { padding:'8px 10px', fontSize:10, fontWeight:700, color:T.txt2, textTransform:'uppercase',
        letterSpacing:'.04em', whiteSpace:'nowrap', borderBottom:`2px solid ${T.border}`,
        background:'#f8fafc', borderRight:`1px solid ${T.border}`, textAlign:'center' }
    const tdS = { padding:'8px 10px', fontSize:12, color:T.txt, borderBottom:`1px solid ${T.border}`,
        borderRight:`1px solid ${T.border}`, verticalAlign:'middle', whiteSpace:'nowrap' }
    const tdR = { ...tdS, textAlign:'right', fontVariantNumeric:'tabular-nums', fontFamily:'monospace', fontSize:11 }

    return (
        <div style={{ minHeight:'100vh', background:T.bg, fontFamily:T.font, padding:'24px 28px 64px' }}>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
                @keyframes spin { to { transform:rotate(360deg) } }
                tr:hover td { background: #eff6ff !important; }
            `}</style>

            <div style={{ maxWidth:'100%' }}>

                {/* ── Header ── */}
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20, flexWrap:'wrap', gap:12 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                        <button onClick={() => history.goBack()}
                            style={{ width:34, height:34, borderRadius:9, border:`1px solid ${T.border}`,
                                background:T.card, cursor:'pointer', fontSize:16, color:T.txt2,
                                display:'flex', alignItems:'center', justifyContent:'center',
                                boxShadow:T.shadow, flexShrink:0, fontFamily:T.font, transition:'all .15s' }}
                            onMouseEnter={e => { e.currentTarget.style.background=T.navy; e.currentTarget.style.color='white' }}
                            onMouseLeave={e => { e.currentTarget.style.background=T.card; e.currentTarget.style.color=T.txt2 }}>
                            ‹
                        </button>
                        <div>
                            <h1 style={{ margin:0, fontSize:22, fontWeight:800, color:T.navy, letterSpacing:'-0.025em' }}>
                                Relación de Declaraciones
                            </h1>
                            <p style={{ margin:'3px 0 0', fontSize:13, color:T.txt2 }}>
                                {sociedad ? `${sociedad.razonSocial} · ${sociedad.rfc}` : 'Selecciona una sociedad para ver sus declaraciones'}
                            </p>
                        </div>
                    </div>
                    <button onClick={downloadExcel} disabled={declaraciones.length === 0}
                        style={{ display:'inline-flex', alignItems:'center', gap:8, padding:'9px 18px',
                            borderRadius:T.rsm, border:'none', background: declaraciones.length === 0 ? T.muted : T.blue,
                            color:'white', fontSize:13, fontWeight:700, cursor: declaraciones.length === 0 ? 'not-allowed':'pointer',
                            fontFamily:T.font, boxShadow: declaraciones.length > 0 ? `0 4px 12px ${T.blue}40`:'none' }}>
                        📥 Descargar Excel
                    </button>
                </div>

                {/* ── Selector sociedad ── */}
                <div style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:T.r,
                    boxShadow:T.shadow, padding:'16px 20px', marginBottom:16 }}>
                    {loadingCat ? (
                        <div style={{ display:'flex', alignItems:'center', gap:8, color:T.txt2, fontSize:13 }}>
                            <div style={{ width:16, height:16, border:`2px solid ${T.border}`, borderTopColor:T.blue,
                                borderRadius:'50%', animation:'spin .7s linear infinite' }} />
                            Cargando sociedades...
                        </div>
                    ) : (
                        <SociedadSelect options={allSociedades} value={sociedad} onChange={handleSociedadChange} />
                    )}
                </div>

                {/* ── Tabla ── */}
                <div style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:T.r, boxShadow:T.shadow, overflow:'hidden' }}>
                    <div style={{ padding:'12px 20px', borderBottom:`1px solid ${T.border}`,
                        display:'flex', alignItems:'center', gap:8 }}>
                        <span>📊</span>
                        <h2 style={{ margin:0, fontSize:14, fontWeight:700, color:T.txt }}>Declaraciones</h2>
                        {declaraciones.length > 0 && (
                            <span style={{ fontSize:12, fontWeight:700, color:T.blue, background:`${T.blue}12`,
                                border:`1px solid ${T.blue}20`, borderRadius:10, padding:'1px 8px' }}>
                                {declaraciones.length}
                            </span>
                        )}
                        {loading && (
                            <div style={{ marginLeft:8, width:16, height:16, border:`2px solid ${T.border}`,
                                borderTopColor:T.blue, borderRadius:'50%', animation:'spin .7s linear infinite' }} />
                        )}
                    </div>

                    {!sociedad ? (
                        <div style={{ padding:'60px 24px', textAlign:'center' }}>
                            <div style={{ fontSize:40, marginBottom:10 }}>🏢</div>
                            <p style={{ margin:0, fontSize:15, fontWeight:600, color:T.txt }}>Selecciona una sociedad</p>
                            <p style={{ margin:'6px 0 0', fontSize:13, color:T.muted }}>Las declaraciones aparecerán aquí automáticamente</p>
                        </div>
                    ) : loading ? (
                        <div style={{ padding:'60px 24px', textAlign:'center' }}>
                            <div style={{ width:36, height:36, border:`3px solid ${T.border}`, borderTopColor:T.blue,
                                borderRadius:'50%', animation:'spin .7s linear infinite', margin:'0 auto 12px' }} />
                            <p style={{ margin:0, fontSize:14, color:T.muted }}>Cargando declaraciones de {sociedad.rfc}...</p>
                        </div>
                    ) : declaraciones.length === 0 ? (
                        <div style={{ padding:'60px 24px', textAlign:'center' }}>
                            <div style={{ fontSize:36, marginBottom:10 }}>📭</div>
                            <p style={{ margin:0, fontSize:14, color:T.muted }}>Sin declaraciones para {sociedad.razonSocial}</p>
                        </div>
                    ) : (
                        <div style={{ overflowX:'auto' }}>
                            <table style={{ borderCollapse:'collapse', minWidth:2200 }}>
                                <thead>
                                    <tr>
                                        {GROUPS.map((g, i) => (
                                            <th key={i} colSpan={g.cols} style={thG(g.bg)}>{g.label}</th>
                                        ))}
                                    </tr>
                                    <tr>
                                        {COL_HEADERS.map(h => <th key={h} style={thS}>{h}</th>)}
                                    </tr>
                                </thead>
                                <tbody>
                                    {declaraciones.map((d, i) => (
                                        <tr key={i}>
                                            <td style={{ ...tdS, background:i%2===0?T.card:T.bg, textAlign:'center', color:T.muted, fontWeight:700 }}>{i+1}</td>
                                            <td style={{ ...tdS, background:i%2===0?T.card:T.bg }}>{d.fechaPresentacionPPM || '—'}</td>
                                            <td style={{ ...tdS, background:i%2===0?T.card:T.bg }}>
                                                <span style={{ fontWeight:700 }}>{d.periodo}/{d.ejercicio}</span>
                                                <br/><span style={{ fontSize:11, color:T.txt2 }}>{d.fechaUltimaPresentacionPeriodoEjercicio}</span>
                                            </td>
                                            <td style={{ ...tdS, background:i%2===0?T.card:T.bg, maxWidth:200 }}>
                                                {d.declaracionesProvisonalesDelPeriodoEjercicio?.map((dp, dpi) => (
                                                    <div key={dpi} style={{ marginBottom:4 }}>
                                                        <span style={{ fontSize:11, color:T.txt2 }}>{dp.tipoDeclaracion} · {dp.fechaHora}</span>
                                                        {dp.documentFileUrl && (
                                                            <button onClick={() => ElementsDataService.downloadS3File(
                                                                `?fileName=${dp.documentFileUrl}`,
                                                                `${d.rfc}_${dp.tipoDeclaracion}_${d.periodo}_${d.ejercicio}.pdf`
                                                            )} style={{ marginLeft:6, padding:'1px 8px', borderRadius:5,
                                                                border:`1px solid ${T.greenBdr}`, background:T.greenBg,
                                                                color:T.green, fontSize:11, cursor:'pointer', fontFamily:T.font }}>
                                                                ⬇
                                                            </button>
                                                        )}
                                                    </div>
                                                )) || '—'}
                                            </td>
                                            {/* Contable */}
                                            <td style={{ ...tdR, background:i%2===0?T.card:T.bg }}>{fmt(d.ingresosNominalesMesDeclarado)}</td>
                                            <td style={{ ...tdR, background:i%2===0?T.card:T.bg }}>{fmt(d.ingresosNominalesFacturados)}</td>
                                            <td style={{ ...tdR, background:i%2===0?T.card:T.bg }}>{fmt(d.ingresosNominalesaDisminuir)}</td>
                                            <td style={{ ...tdR, background:i%2===0?T.card:T.bg }}>{fmt(d.cfdisIngresosMetadadata)}</td>
                                            <td style={{ ...tdR, background:i%2===0?T.card:T.bg }}>{fmt(d.totalIngresosNominales)}</td>
                                            <td style={{ ...tdR, background:i%2===0?T.card:T.bg }}>{fmt(d.cfdisIngresosAcumuladosMetadadata)}</td>
                                            <td style={{ ...tdR, background:i%2===0?T.card:T.bg }}>{fmt(d.cfdisNominaMetadadata)}</td>
                                            <td style={{ ...tdR, background:i%2===0?T.card:T.bg }}>{fmt(d.cfdisNominaAcumuladaMetadadata)}</td>
                                            {/* R1 */}
                                            <td style={{ ...tdR, background:i%2===0?T.card:T.bg }}>{fmt(d.r1Acargo)}</td>
                                            <td style={{ ...tdR, background:i%2===0?T.card:T.bg }}>{fmt(d.r1Recargos)}</td>
                                            <td style={{ ...tdR, background:i%2===0?T.card:T.bg }}>{fmt(d.r1CantidadAPagar)}</td>
                                            {/* R12 */}
                                            <td style={{ ...tdR, background:i%2===0?T.card:T.bg }}>{fmt(d.r12Acargo)}</td>
                                            <td style={{ ...tdR, background:i%2===0?T.card:T.bg }}>{fmt(d.r12Recargos)}</td>
                                            <td style={{ ...tdR, background:i%2===0?T.card:T.bg }}>{fmt(d.r12Subsidio)}</td>
                                            <td style={{ ...tdR, background:i%2===0?T.card:T.bg }}>{fmt(d.r12CantidadAPagar)}</td>
                                            {/* R13 */}
                                            <td style={{ ...tdR, background:i%2===0?T.card:T.bg }}>{fmt(d.r13Acargo)}</td>
                                            <td style={{ ...tdR, background:i%2===0?T.card:T.bg }}>{fmt(d.r13Recargos)}</td>
                                            <td style={{ ...tdR, background:i%2===0?T.card:T.bg }}>{fmt(d.r13Subsidio)}</td>
                                            <td style={{ ...tdR, background:i%2===0?T.card:T.bg }}>{fmt(d.r13CantidadAPagar)}</td>
                                            {/* R14 */}
                                            <td style={{ ...tdR, background:i%2===0?T.card:T.bg }}>{fmt(d.r14Acargo)}</td>
                                            <td style={{ ...tdR, background:i%2===0?T.card:T.bg }}>{fmt(d.r14Recargos)}</td>
                                            <td style={{ ...tdR, background:i%2===0?T.card:T.bg }}>{fmt(d.r14CantidadAPagar)}</td>
                                            {/* R15 */}
                                            <td style={{ ...tdR, background:i%2===0?T.card:T.bg }}>{fmt(d.r15Acargo)}</td>
                                            <td style={{ ...tdR, background:i%2===0?T.card:T.bg }}>{fmt(d.r15Recargos)}</td>
                                            <td style={{ ...tdR, background:i%2===0?T.card:T.bg }}>{fmt(d.r15CantidadAPagar)}</td>
                                            {/* R21 */}
                                            <td style={{ ...tdR, background:i%2===0?T.card:T.bg }}>{fmt(d.r21Acargo)}</td>
                                            <td style={{ ...tdR, background:i%2===0?T.card:T.bg }}>{fmt(d.r21Afavor)}</td>
                                            <td style={{ ...tdR, background:i%2===0?T.card:T.bg }}>{fmt(d.r21Recargos)}</td>
                                            <td style={{ ...tdR, background:i%2===0?T.card:T.bg }}>{fmt(d.r21CantidadAPagar)}</td>
                                            {/* R21 RFN */}
                                            <td style={{ ...tdR, background:i%2===0?T.card:T.bg }}>{fmt(d.r21AcargoRFN)}</td>
                                            <td style={{ ...tdR, background:i%2===0?T.card:T.bg }}>{fmt(d.r21AfavorRFN)}</td>
                                            <td style={{ ...tdR, background:i%2===0?T.card:T.bg }}>{fmt(d.r21RecargosRFN)}</td>
                                            <td style={{ ...tdR, background:i%2===0?T.card:T.bg }}>{fmt(d.r21CantidadAPagarRFN)}</td>
                                            {/* R24 */}
                                            <td style={{ ...tdR, background:i%2===0?T.card:T.bg }}>{fmt(d.r24Acargo)}</td>
                                            <td style={{ ...tdR, background:i%2===0?T.card:T.bg }}>{fmt(d.r24Recargos)}</td>
                                            <td style={{ ...tdR, background:i%2===0?T.card:T.bg }}>{fmt(d.r24CantidadAPagar)}</td>
                                            {/* IVA SC */}
                                            <td style={{ ...tdR, background:i%2===0?T.card:T.bg }}>{fmt(d.ivaSimplificadoConfianzaAcargo)}</td>
                                            <td style={{ ...tdR, background:i%2===0?T.card:T.bg }}>{fmt(d.ivaSimplificadoConfianzaAfavor)}</td>
                                            <td style={{ ...tdR, background:i%2===0?T.card:T.bg }}>{fmt(d.ivaSimplificadoConfianzaRecargos)}</td>
                                            <td style={{ ...tdR, background:i%2===0?T.card:T.bg }}>{fmt(d.ivaSimplificadoConfianzaCantidadAPagar)}</td>
                                            {/* ISR SC */}
                                            <td style={{ ...tdR, background:i%2===0?T.card:T.bg }}>{fmt(d.isrSimplificadoConfianzaAcargo)}</td>
                                            <td style={{ ...tdR, background:i%2===0?T.card:T.bg }}>{fmt(d.isrSimplificadoConfianzaRecargos)}</td>
                                            <td style={{ ...tdR, background:i%2===0?T.card:T.bg }}>{fmt(d.isrSimplificadoConfianzaCantidadAPagar)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

            </div>
        </div>
    )
}
